import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  signal,
  effect,
  Injector,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  EmptyComponent,
  GridCardComponent,
  type GridCardItem,
  SkeletonComponent,
} from 'nebula-ui-kit';
import { catchError, filter, forkJoin, of, switchMap, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import {
  AuthService,
  formatVaultLocalDateTime,
  resolveVaultDashboardRoleFallback,
  vaultCurrentUserIdFromSession,
  readPersistedVaultUserId,
  VaultAuthService,
  VaultOperativeRoleSyncService,
  VaultSessionService,
  VAULT_ADMIN_USERS_CHANGED_EVENT,
  VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT,
  type VaultActiveTenantContext,
} from '@nebula/shared-vault/data-access';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import { FileAssignmentService } from '../../../../data-access/src/lib/repository/file-assignment.service';
import { PreservationMessagesService } from '../../../../data-access/src/lib/preservation-messages/preservation-messages.service';
import type { DashboardStatsResponse } from '../../../../domain/src/lib/dashboard/dashboard.model';
import type { FileAssignmentRecord } from '../../../../domain/src/lib/repository/file-assignment.model';
import type { VaultPreservationMessage } from '../../../../domain/src/lib/preservation-messages/preservation-message.model';
import {
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
} from '@nebula-shared/ui';
import { refreshCurrentUserOperativeRoleFromAdminSnapshot } from '../../../../data-access/src/lib/admin-users/admin-user-normalize.util';
import {
  buildDashboardGridItems,
  formatActivityLine,
} from './dashboard-view.utils';
import {
  buildDashboardTasksFromFileAssignments,
  mergeDashboardTaskRows,
  resolveDashboardTaskNavigation,
} from './dashboard-task-navigation.util';
import {
  hasFullRepositoryBrowseAccess,
  resolveMyFileAssignmentsFromApi,
} from '../repository-admin/repository-assignment-access.util';

interface DashboardTaskView {
  id: string;
  title: string;
  description: string;
  moduleLabel: string;
  priorityLabel: string;
  whenLabel: string;
  route: string[];
  queryParams: Record<string, string>;
  linkLabel: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    GridCardComponent,
    EmptyComponent,
    SkeletonComponent,
    RouterLink,
    VaultModuleShellComponent,
  ],
})
export class DashboardComponent {
  private readonly dashboardService = inject(DashboardService);
  private readonly fileAssignmentService = inject(FileAssignmentService);
  private readonly preservationMessagesService = inject(PreservationMessagesService);
  private readonly auth = inject(VaultAuthService);
  private readonly sessionAuth = inject(AuthService);
  private readonly vaultSession = inject(VaultSessionService);
  private readonly operativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private static readonly MODULE_LABEL = 'Inicio';

  private assignmentRefreshSeq = 0;
  private assignmentUserIdRetryTimer: ReturnType<typeof setTimeout> | null = null;

  readonly loading = signal(true);
  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly stats = signal<DashboardStatsResponse | null>(null);

  readonly preservationMessages = signal<VaultPreservationMessage[]>([]);
  readonly preservationIndex = signal(0);

  private readonly tasksRemote = toSignal(
    this.dashboardService.getTasks().pipe(
      catchError(() => of<Record<string, unknown>[]>([])),
      map((rows) => rows ?? []),
    ),
    { initialValue: [] as Record<string, unknown>[] },
  );

  private readonly myFileAssignments = signal<FileAssignmentRecord[]>([]);

  readonly gridItems = computed<GridCardItem[]>(() =>
    buildDashboardGridItems(this.stats()),
  );

  readonly activityRows = computed(() => {
    const s = this.stats();
    const fromStats = s?.recent_activity ?? [];
    const merged = [...fromStats];
    return merged.slice(0, 20);
  });

  readonly taskRows = computed(() => {
    const s = this.stats();
    const embedded = s?.tasks ?? [];
    const remote = this.tasksRemote();
    const apiTasks = embedded?.length ? embedded : remote;
    const userId = vaultCurrentUserIdFromSession() ?? '';
    const assignmentTasks = buildDashboardTasksFromFileAssignments(
      this.myFileAssignments(),
      userId,
    );
    return mergeDashboardTaskRows(assignmentTasks, apiTasks).slice(0, 20);
  });

  readonly taskCards = computed<DashboardTaskView[]>(() =>
    this.taskRows().map((row, idx) => this.toTaskCard(row, idx)),
  );

  readonly userDisplayName = computed(() => {
    const data = this.auth.getUserData();
    if (!data || typeof data !== 'object') return 'Usuario';
    const rec = data as Record<string, unknown>;
    const fullName = typeof rec['full_name'] === 'string' ? rec['full_name'].trim() : '';
    if (fullName) return fullName;
    const name = typeof rec['name'] === 'string' ? rec['name'].trim() : '';
    if (name) return name;
    const email = typeof rec['email'] === 'string' ? rec['email'].trim() : '';
    if (email) return email.split('@')[0] || email;
    return 'Usuario';
  });

  readonly sessionContext = signal<VaultActiveTenantContext>(
    this.vaultSession.getActiveTenantContext(),
  );

  readonly sessionSummary = computed(() => {
    const ctx = this.sessionContext();
    if (ctx.isGlobalScope) {
      return `Rol: ${ctx.roleLabel} · Alcance: ${ctx.scopeLabel}`;
    }
    return `Rol: ${ctx.roleLabel} · Empresa: ${ctx.companyLabel}`;
  });

  readonly preservationCarouselLabel = computed(() => {
    const list = this.preservationMessages();
    if (!list.length) return '';
    return `Mensaje ${this.preservationIndex() + 1} de ${list.length}`;
  });

  readonly currentPreservationMessage = computed(() => {
    const list = this.preservationMessages();
    if (!list.length) return null;
    const i = this.preservationIndex() % list.length;
    return list[i];
  });

  constructor() {
    this.refreshDashboardSessionContext();
    this.refreshAssignmentTasks(true);

    if (typeof window !== 'undefined') {
      const refreshSession = () => {
        this.refreshDashboardSessionContext();
        this.refreshAssignmentTasks(true);
      };
      const onAdminUsersChanged = () => this.refreshDashboardSessionContext();
      const onFileAssignmentsChanged = () => this.refreshAssignmentTasks(true);
      const onVisibilityChange = () => {
        if (document.visibilityState !== 'visible' || hasFullRepositoryBrowseAccess()) {
          return;
        }
        this.refreshAssignmentTasks(true);
      };
      window.addEventListener('vault-session-updated', refreshSession);
      window.addEventListener(VAULT_ADMIN_USERS_CHANGED_EVENT, onAdminUsersChanged);
      window.addEventListener(VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT, onFileAssignmentsChanged);
      window.addEventListener('storage', this.onStorageSessionChanged);
      document.addEventListener('visibilitychange', onVisibilityChange);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('vault-session-updated', refreshSession);
        window.removeEventListener(VAULT_ADMIN_USERS_CHANGED_EVENT, onAdminUsersChanged);
        window.removeEventListener(VAULT_FILE_ASSIGNMENTS_CHANGED_EVENT, onFileAssignmentsChanged);
        window.removeEventListener('storage', this.onStorageSessionChanged);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (this.assignmentUserIdRetryTimer) {
          clearTimeout(this.assignmentUserIdRetryTimer);
          this.assignmentUserIdRetryTimer = null;
        }
      });
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes('/dashboard')) {
          this.refreshDashboardSessionContext();
          this.refreshAssignmentTasks(true);
        }
      });

    this.operativeRoleSync
      .syncFromApi({ force: true })
      .pipe(
        switchMap(() =>
          forkJoin({
            stats: this.dashboardService.getStats().pipe(
              catchError((err: unknown) => {
                applyVaultModuleInitialLoadErrorSignals(
                  err,
                  {
                    moduleLabel: DashboardComponent.MODULE_LABEL,
                    fallback: 'No se pudieron cargar las estadísticas del dashboard.',
                  },
                  {
                    accessDenied: this.accessDenied,
                    loadError: this.loadError,
                    loadErrorTitle: this.loadErrorTitle,
                  },
                );
                return of<DashboardStatsResponse | null>(null);
              }),
            ),
            preservation: this.preservationMessagesService.active().pipe(
              catchError(() => of<VaultPreservationMessage[]>([])),
            ),
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ stats: data, preservation }) => {
          this.stats.set(data);
          this.refreshDashboardSessionContext();
          this.refreshAssignmentTasks(true);
          this.preservationMessages.set(Array.isArray(preservation) ? preservation : []);
          this.preservationIndex.set(0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });

    effect(
      (onCleanup) => {
        const list = this.preservationMessages();
        if (list.length <= 1) return;

        const id = globalThis.setInterval(() => {
          this.preservationIndex.update((n) => (n + 1) % list.length);
        }, 8000);

        onCleanup(() => globalThis.clearInterval(id));
      },
      { injector: this.injector },
    );
  }

  private readonly onStorageSessionChanged = (event: StorageEvent): void => {
    if (
      event.key !== 'vault_admin_user_assignment_v1' &&
      event.key !== 'vault_session_pin_v1' &&
      event.key !== 'vault_operative_role' &&
      event.key !== 'user_data' &&
      event.key !== 'access_token'
    ) {
      return;
    }
    this.refreshDashboardSessionContext();
    if (
      event.key === 'user_data' ||
      event.key === 'access_token' ||
      event.key === 'vault_operative_role'
    ) {
      this.refreshAssignmentTasks(true);
    }
  };

  private refreshDashboardSessionContext(): void {
    refreshCurrentUserOperativeRoleFromAdminSnapshot();
    this.sessionAuth.ensureCurrentUserFromToken();
    this.sessionContext.set(
      this.vaultSession.getActiveTenantContext(
        resolveVaultDashboardRoleFallback(this.stats()?.role),
      ),
    );
  }

  private refreshAssignmentTasks(force = false): void {
    const seq = ++this.assignmentRefreshSeq;
    this.operativeRoleSync
      .syncFromApi({ force: true })
      .pipe(take(1))
      .subscribe(() => {
        if (seq !== this.assignmentRefreshSeq) {
          return;
        }
        this.sessionAuth.ensureCurrentUserFromToken();
        const vaultUserId = readPersistedVaultUserId();
        const userId = vaultUserId || vaultCurrentUserIdFromSession();
        if (!userId) {
          this.scheduleAssignmentTasksRetry(seq, force);
          return;
        }
        if (!vaultUserId) {
          this.scheduleAssignmentTasksRetry(seq, force);
          return;
        }
        if (hasFullRepositoryBrowseAccess()) {
          this.myFileAssignments.set([]);
          return;
        }
        this.fileAssignmentService
          .getAssigned(vaultUserId, undefined, {
            force,
            cacheNamespace: `session:${vaultUserId}`,
          })
          .pipe(
            take(1),
            map((rows) =>
              resolveMyFileAssignmentsFromApi(rows, vaultUserId, undefined, {
                trustApiScope: true,
              }),
            ),
            catchError(() => of<FileAssignmentRecord[]>([])),
          )
          .subscribe((rows) => {
            if (seq !== this.assignmentRefreshSeq) {
              return;
            }
            this.myFileAssignments.set(rows);
          });
      });
  }

  private scheduleAssignmentTasksRetry(seq: number, force: boolean, attempt = 0): void {
    if (seq !== this.assignmentRefreshSeq || attempt >= 12) {
      return;
    }
    if (this.assignmentUserIdRetryTimer) {
      clearTimeout(this.assignmentUserIdRetryTimer);
    }
    this.assignmentUserIdRetryTimer = setTimeout(() => {
      this.assignmentUserIdRetryTimer = null;
      if (seq !== this.assignmentRefreshSeq) {
        return;
      }
      this.sessionAuth.ensureCurrentUserFromToken();
      if (readPersistedVaultUserId()) {
        this.refreshAssignmentTasks(force);
        return;
      }
      this.scheduleAssignmentTasksRetry(seq, force, attempt + 1);
    }, 100 + attempt * 80);
  }

  protected formatActivity(row: Record<string, unknown>): string {
    return formatActivityLine(row);
  }

  protected formatTask(row: Record<string, unknown>): string {
    return formatActivityLine(row);
  }

  private toTaskCard(row: Record<string, unknown>, idx: number): DashboardTaskView {
    const id = this.readString(row, ['id']) || `task-${idx}`;
    const title =
      this.readString(row, ['title']) ||
      this.readString(row, ['message']) ||
      this.readString(row, ['action']) ||
      'Tarea pendiente';
    const description =
      this.readString(row, ['description']) ||
      'Abrí el módulo para revisar el detalle y completar la acción.';
    const navigation = resolveDashboardTaskNavigation(row);
    const priorityRaw = this.readString(row, ['priority'])?.toLowerCase() ?? '';
    const priorityLabel =
      priorityRaw === 'high'
        ? 'Alta'
        : priorityRaw === 'medium'
          ? 'Media'
          : priorityRaw === 'low'
            ? 'Baja'
            : 'Normal';
    const whenSource =
      this.readString(row, ['created_at']) ||
      this.readString(row, ['date']) ||
      this.readString(row, ['due_date']);
    const whenLabel = whenSource ? this.formatDate(whenSource) : 'Sin fecha';

    return {
      id,
      title,
      description,
      moduleLabel: navigation.moduleLabel,
      priorityLabel,
      whenLabel,
      route: navigation.route,
      queryParams: navigation.queryParams,
      linkLabel: navigation.linkLabel,
    };
  }

  private readString(row: Record<string, unknown>, keys: string[]): string | null {
    for (const k of keys) {
      const v = row[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  }

  private formatDate(value: string): string {
    return formatVaultLocalDateTime(value);
  }

  selectPreservationSlide(i: number): void {
    this.preservationIndex.set(i);
  }
}
